import React, { useState, useRef } from 'react'
import { parseBits } from '../../Util/parsebits';
import './filepicker.css'
/** * FilePicker component that allows users to upload images from their device, paste images from the clipboard, or load images from a URL.
 * This component handles file selection, image loading, and error handling.
 * @param {Object} props - The properties passed to the FilePicker component.
 * @param {function} props.onFileSelect - Callback function to handle the selected file data.
 * @param {React.Ref} props.containerRef - Reference to the container element for styling or layout purposes.
 * @returns {JSX.Element} The rendered FilePicker component.
 */
const  FilePicker = ({ onFileSelect, containerRef })=> {
  //asconst [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const extUrl = useRef(null);
  const filePaste = useRef(null);
  const fileInput = useRef(null);

  // Triggered when user presses button, then input.click() is triggered on hidden input 
  // Error entry/reset point
  function handleFileSelect(e){
    setErrors(null)
    const file = e.target.files[0];
    if (!file){
      console.error(`No File ${e}`)
      setErrors([`There was a problem with the file. Please try again.`])
      return;
    }
    LoadFile(file, {
      origin: 'Selected from device'
    });
  }

  function handleFilePaste(e){
    setErrors(null)
    const file = e.clipboardData.files[0];
    if (!file){
      console.error(`No File ${e}`)
      setErrors([`There was a problem with the file. Please try again.`])
      return;
    }    
    LoadFile(file, {
      fileName: 'pasted.png',
      origin: 'pasted from clipboard'
    });
  }

  // Paste event *
  // Error entry/reset point
  function handleLoadExternal(e){
    setErrors(null);
    const url = e.clipboardData.getData('Text');   
    setLoading(true); 
    fetch( url/* , { mode: 'no-cors' } */)
      .then((response) =>{ 
        if ( !response.ok ) throw new Error(`Response not OK (${response.statusText})`);
        return response.blob();
      })
      .then((blob) => {
        if (blob.type.includes('image')){
          LoadFile(blob, {
            origin: url,
            fileName: 'image.png'
          });
        }else{
          throw new Error(`Response not an image (${blob.type})`);
        }        
      })
      .catch( error => {        
        setErrors([`Error fetching url:\n${error}`]);
        console.error(`Fetch ${url}\n [${error}]`);
      })
      .finally( ()=>{
        setLoading(false);
        extUrl.current.value = '';
      });
  }

  // Reader.onloadend
  function LoadFile( file, optional={} ){
    try{
      setLoading(true); 
      const reader = new FileReader();
      reader.onerror = e => {
        setLoading(false);
        throw reader.error
      }
      reader.onloadend = (e) => {        
        const imgContent = e.target.result;
        const img = new Image();
        img.onload = img_e =>{          
          let data = {
            fileName: file.name,          
            size: parseBits(file.size),
            type: file.type,
            width: img_e.target.width,
            height: img_e.target.height,
            content: imgContent
          };
          data = {...data, ...optional};
          onFileSelect(data);
          //setData(data);
          setLoading(false);
        }
        img.src = imgContent;    
      }
      reader.readAsDataURL(file);
    }catch(E){
      setErrors([`Error loading file:\n${E}`])
      setLoading(false);
      console.error(`LoadFile Error: ${E}`);
    }    
  }

  return (
    <div className='form-list' ref={containerRef} >

    { loading && (<p>Loading...</p>) }

    { errors && errors.map((error, i)=>{
        return ( <div className='' key={i}>{`${error}`}</div> )
    })}
      <div className='title-container'>
        <h4>Upload Image</h4>
        <small>upload an image from your device, a url, or just paste it in.</small>
      </div>

      <div className='button-grid'>
        <button
          id='file-select'
          onClick={e=>fileInput.current.click()}
        >Select</button>
        <input 
          id='file-input'
          ref={fileInput}
          type='file' 
          accept='image/*'
          onChange={handleFileSelect}        
          className='visually-hidden'
        ></input>
        <span>From your device</span>

        <input 
          ref={extUrl}
          onPaste={handleLoadExternal}
          name='ext-url' 
          type='text'
          placeholder='URL'
        ></input>
        <span>Paste url from web</span>

        <input 
          ref={filePaste}
          onPaste={handleFilePaste}
          name='file-paste' 
          type='text'
          placeholder='File'
        ></input>
        <span>Paste file from clipboard</span>

      </div>  

    </div>
  );
};

export default FilePicker;